import { useContext, useEffect, useMemo, useState } from 'react'
import { useParams, withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { Alert, Badge, Button, CloseButton, Modal, ProgressBar } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { CheckboxField, InputField, SelectField, TimePickerField } from 'components/shared/input'
import { Flex } from 'components/shared/flex'
import { RackVisualizer } from 'components/rack/rack_visualizer'
import { useFetchLastLocationApi, useFetchSamplesApi, usePutInspectSampleApi } from 'api/sample'
import { SampleForm, SampleSearchForm } from 'entities/sample'
import { useFetchTransportationReportsApi } from 'api/transportation_report'
import { ExTable, TableCell, TableWrapper } from 'components/shared/widget'
import { useFetchRacksApi } from 'api/rack'
import { TransportationReportSearchForm } from 'entities/transportation_report'
import { useFetchRequestFormsApi } from 'api/request_form'
import { RequestFormSearchForm } from 'entities/request_form'
import _ from 'lodash'
import { formatRequestFormCode } from 'utils/formatRequestFormCode'

type Props = {
    history: H.History
}

const ReceiptPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const { id } = useParams<{ id: string }>()
    const query = useQuery<{ ids: string }>()
    const lastLocationApi = useFetchLastLocationApi()
    const form = useForm<SampleForm[]>([])
    const inspectApi = usePutInspectSampleApi()
    const [noProblem, setNoProblem] = useState<boolean | undefined>()
    const transportationReportSearchForm = useForm<TransportationReportSearchForm>({ id: query.ids })
    const transportationReportsApi = useFetchTransportationReportsApi(transportationReportSearchForm)
    const [showConfirm, setShowConfirm] = useState<boolean>(false)
    const samplesSearchForm = useForm<SampleSearchForm>({ sampleStatus: 'INTERVIEWED,RECOLLECT' })
    const samplesApi = useFetchSamplesApi(samplesSearchForm)
    const existSamplesSearchForm = useForm<SampleSearchForm>({ transportationReportIdIn: query.ids, ordering: 'sample_inspection_date', perPage: 200 })
    const existSamplesApi = useFetchSamplesApi(existSamplesSearchForm)
    const racksApi = useFetchRacksApi()
    const addRowForm = useForm<{ count: number; transportationReportId?: number }>({ count: 1 })
    const stillInterviewRequestFormsForm = useForm<RequestFormSearchForm>({ transportationReportIdIn: query.ids })
    const stillInterviewRequestFormsApi = useFetchRequestFormsApi(stillInterviewRequestFormsForm)
    const [showConfirmRack, setShowConfirmRack] = useState<boolean>(false)
    const [isCheckedRack, setIsCheckedRack] = useState<boolean>(false)
    const isHomeTest = process.env.REACT_APP_IS_HOME_TEST

    useEffect(() => {
        transportationReportsApi.execute()
        existSamplesApi.execute()
        stillInterviewRequestFormsApi.execute()
        racksApi.execute({
            ordering: 'rack_location_code',
        })
        lastLocationApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(inspectApi.loading)
    }, [inspectApi.loading])

    useEffectSkipFirst(() => {
        if (existSamplesApi.isSuccess()) {
            addRowForm.set({ count: existSamplesApi.response.results.length })
            form.set([
                ...existSamplesApi.response.results.map((s) => {
                    const sampleForm = s as SampleForm
                    sampleForm.tubeBarcode = sampleForm.requestForm?.code
                    sampleForm.hasNoProblem = true
                    delete s.resultPdf
                    delete s.results
                    return s
                }),
            ])
        }
    }, [existSamplesApi.loading])

    useEffectSkipFirst(() => {
        if (samplesApi.isSuccess()) {
            const sample = samplesApi.response.results[0]

            if (sample && sample.requestForm?.code) {
                form.update((f) => {
                    f.map((s) => {
                        if (!s.tubeBarcode) {
                            return
                        }
                        if (formatRequestFormCode(s.tubeBarcode) === sample.requestForm?.code) {
                            s.patient = { ...sample.patient }
                            s.background = { ...sample.background }
                            s.requestForm = { ...sample.requestForm }
                            s.id = sample.id
                            s.followupStudyAgreement = sample.followupStudyAgreement
                            s.isRecollect = sample.isRecollect
                            s.isChilledTransport = sample.isChilledTransport
                        }
                    })
                })
            } else {
                globalState.showConfirm(
                    <div>
                        バーコードに一致する検体情報がありませんでした。
                        <br />
                        WEB問診が終わっていないか、バーコードが間違っています
                        <br />
                        バーコードが間違っていない場合、このまま保存してください
                    </div>
                )
            }
        }
    }, [samplesApi.loading])

    function getCellNumber(cell: string): number {
        const column = cell.charCodeAt(0) - 'A'.charCodeAt(0) + 1
        const row = parseInt(cell.substring(1), 10)
        return (column - 1) * 7 + row
    }
    function getCellString(cellNumber: number): string {
        const row = ((cellNumber - 1) % 7) + 1
        const column = Math.floor((cellNumber - 1) / 7) + 'A'.charCodeAt(0)
        const columnString = String.fromCharCode(column)
        return columnString + row.toString()
    }

    const nextRack = (currentRack: string, isPcrSample: boolean) => {
        const ngsRacks = racksApi.response.results.filter((r) => r.rackType?.name === '8x12')
        const pcrRacks = racksApi.response.results.filter((r) => r.rackType?.name === '7x7')
        const targetRacks = isPcrSample ? pcrRacks : ngsRacks
        const index = targetRacks.findIndex((r) => r.locationCode === currentRack)

        if (targetRacks.length - 1 === index) {
            return targetRacks[0].locationCode!
        } else {
            return targetRacks[index + 1].locationCode!
        }
    }

    const getLocationCount = (isPcrSample: boolean) => {
        if (isPcrSample) {
            return 49
        } else {
            return 96
        }
    }

    const getNextLocation = (index: number, newItemCount: number, isPcrSample: boolean) => {
        const lastLocationRack = isPcrSample ? lastLocationApi.response.pcrRack : lastLocationApi.response.ngsRack

        const tubeLocation = lastLocationRack?.tubeLocation
        if (!tubeLocation) {
            return
        }
        let numberTubeLocation: number
        if (!tubeLocation?.match(/^\d+$/g)) {
            numberTubeLocation = getCellNumber(tubeLocation)
        } else {
            numberTubeLocation = Number(tubeLocation)
        }

        let nextTubeLocation = numberTubeLocation + newItemCount
        let locationCode: string

        const LOCATION_COUNT = getLocationCount(isPcrSample)
        if (nextTubeLocation > LOCATION_COUNT) {
            locationCode = nextRack(lastLocationRack.rack.locationCode!, isPcrSample)
            nextTubeLocation = nextTubeLocation - LOCATION_COUNT
        } else {
            locationCode = lastLocationRack.rack.locationCode!
        }

        if (!hasProblem(form.object[index])) {
            const rackId = racksApi.response.results.find((r) => r.locationCode === locationCode)?.id
            const tubeLocation = isPcrSample ? getCellString(nextTubeLocation) : String(nextTubeLocation)
            const freezerLocation = locationCode
            return { rackId, tubeLocation, freezerLocation }
        }
    }

    // 編集中かどうかをstateで管理
    const [isEdited, setIsEdited] = useState(false)

    const handlePopstate = () => {
        const isDiscardedOK = confirm('保存されていないデータは削除されますが、よろしいですか？')
        if (isDiscardedOK) {
            // OKの場合、historyAPIで戻るを実行します。
            window.history.back()
            setIsEdited(false)
        } else {
            // キャンセルの場合、 ダミー履歴を挿入して「戻る」を1回分吸収できる状態にする
            window.history.pushState(null, '', null)
        }
    }

    useEffect(() => {
        window.history.pushState(null, '', null)
        window.addEventListener('popstate', handlePopstate, false)

        return () => {
            window.removeEventListener('popstate', handlePopstate, false)
        }
    }, [])

    useEffectSkipFirst(() => {
        setIsEdited(true)
    }, [form.object])

    const onChangeException = (e?: React.ChangeEvent<HTMLInputElement>, index?: number, impossibleExam?: boolean) => {
        if (e?.target.checked && index !== undefined) {
            form.update((f) => {
                f[index].hasNoProblem = false
                f[index].checkedPatient = false
            })
        }
    }

    const isCheckedStatus = (index: number) => {
        if (form.object[index].hasNoProblem) {
            return true
        } else if (
            form.object[index].includeBlood ||
            form.object[index].nonUrine ||
            form.object[index].differentContainer ||
            form.object[index].otherAbnormalities
        ) {
            return true
        }
        return false
    }

    const hasProblem = (sample: SampleForm) => {
        if (sample.hasNoProblem) {
            return false
        } else if (sample.nonUrine || sample.differentContainer || sample.otherAbnormalities) {
            return true
        } else if (sample.quantity !== undefined && sample.quantity < 1) {
            return true
        }
        return false
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(inspectApi.loading)
        if (inspectApi.isSuccess()) {
            window.location.reload()
        }
    }, [inspectApi.loading])

    const updateTubeLocations = () => {
        let newPcrItemCount = 0
        let newNgsItemCount = 0

        const copy = [...form.object]
        copy.forEach((sample, index) => {
            const isInspected = sample.inspectionStatus === 'INSPECTED'
            if (isInspected || !sample.patient || hasProblem(sample)) {
                return
            }
            // !沖縄県の患者の場合、PCRサンプルとして扱う
            const isPcrSample = !form.object[index].isChilledTransport && sample.patient.address?.prefecture === '沖縄県'
            if (isPcrSample) {
                newPcrItemCount++
            } else {
                newNgsItemCount++
            }
            const nextLocation = getNextLocation(index, isPcrSample ? newPcrItemCount : newNgsItemCount, isPcrSample)
            if (nextLocation) {
                sample.rackId = nextLocation.rackId
                sample.tubeLocation = nextLocation.tubeLocation
                sample.freezerLocation = nextLocation.freezerLocation
            }
        })
        form.set(copy)
    }

    function focusNextElement(nextTabIndex: number): void {
        const nextElement = document.querySelector(`[tabindex="${nextTabIndex}"]`) as HTMLElement
        if (nextElement) {
            nextElement.focus()
        }
    }

    useEffectSkipFirst(() => {
        form.update((f) => {
            f.forEach((sample) => {
                if (sample.inspectionStatus !== 'INSPECTED') {
                    sample.transportationReportId = addRowForm.object.transportationReportId
                }
            })
        })
    }, [addRowForm.object.transportationReportId])

    return (
        <>
            <Modal show={showConfirmRack} onHide={() => setShowConfirmRack(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>ラック確認</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {_.uniq(form.object.map((s) => s.rackId)).map((rackId) => {
                            return (
                                <RackVisualizer
                                    key={'rack-' + rackId}
                                    rackId={String(rackId)}
                                    isPcrRack={racksApi.response.results.filter((r) => r.id === rackId && r.rackType?.name === '7x7').length > 0}
                                    newLocationSamples={form.object.filter((s) => s.inspectionStatus !== 'INSPECTED' && s.rackId === rackId)}
                                />
                            )
                        })}
                    </div>
                    <div>
                        <Flex alignItems="center">
                            <input
                                id="isCheckedRack"
                                type="checkbox"
                                onChange={(e) => {
                                    setIsCheckedRack(e.target.checked)
                                }}
                            ></input>
                            <label htmlFor="isCheckedRack">確認しました</label>
                        </Flex>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmRack(false)}>
                        キャンセル
                    </Button>
                    <Button
                        variant="success"
                        disabled={!isCheckedRack}
                        onClick={() => {
                            inspectApi.execute(form)
                        }}
                    >
                        保存
                    </Button>
                </Modal.Footer>
            </Modal>
            <ContentHeader
                headerTitle="検品 - 入庫"
                left={
                    <Flex alignItems="center">
                        <div style={{ width: 150 }}>
                            <InputField
                                form={addRowForm}
                                attr="count"
                                style={{ marginBottom: 0 }}
                                type="number"
                                min={existSamplesApi.response.results.length}
                                max={transportationReportsApi.response.results.reduce((a, b) => a + (b.spitzCount ?? 0), 0)}
                            />
                        </div>
                        <Button
                            style={{ width: 100 }}
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                if (addRowForm.object.count > form.object.length) {
                                    form.update((f) => {
                                        for (let i = 0; i < addRowForm.object.count - form.object.length; i++) {
                                            f.push({ quantity: 10 })
                                        }
                                    })
                                } else {
                                    form.update((f) => {
                                        for (let i = 0; i < form.object.length - addRowForm.object.count; i++) {
                                            const lastElement = f[f.length - 1]
                                            if (lastElement.inspectionStatus === 'INSPECTED') {
                                                break
                                            }
                                            f.pop()
                                        }
                                    })
                                }
                            }}
                        >
                            行設定
                        </Button>
                    </Flex>
                }
                right={
                    <Flex alignItems="center" style={{ width: 700 }}>
                        <div style={{ width: 200 }}>
                            <SelectField
                                form={addRowForm}
                                style={{ marginBottom: 0 }}
                                attr={'transportationReportId'}
                                includeBlank
                                selectItems={transportationReportsApi.response.results.map((tr) => {
                                    return {
                                        label: `ID: ${tr.id} / 伝票番号: ${tr.code}`,
                                        value: tr.id!,
                                    }
                                })}
                            />
                        </div>
                        <Button
                            onClick={() => {
                                form.update((f) => {
                                    f.forEach((sample) => {
                                        sample.hasNoProblem = true
                                    })
                                })
                            }}
                        >
                            すべて異常なし
                        </Button>
                        <ProgressBar
                            max={transportationReportsApi.response.results.reduce((a, b) => a + (b.spitzCount ?? 0), 0)}
                            now={form.object.filter((o) => o.inspectionStatus === 'INSPECTED').length}
                            style={{ flexGrow: 1 }}
                            variant={'success'}
                        />
                        <div>
                            <span>{form.object.filter((o) => o.inspectionStatus === 'INSPECTED').length}</span> /{' '}
                            <span>{transportationReportsApi.response.results.reduce((a, b) => a + (b.spitzCount ?? 0), 0)}</span>
                        </div>

                        <Button
                            variant="success"
                            onClick={() => {
                                setShowConfirmRack(true)
                            }}
                        >
                            保存
                        </Button>
                    </Flex>
                }
            />
            {stillInterviewRequestFormsApi.response.results.filter((r) => !!r.sample).length > 0 && (
                <Alert variant="success">
                    <div>以下の依頼書IDのWEB問診が完了しています</div>
                    {stillInterviewRequestFormsApi.response.results
                        .filter((r) => !!r.sample)
                        .map((r) => {
                            return <div key={`complete-interview-${r.id}`}>{r.code}</div>
                        })}
                </Alert>
            )}
            {stillInterviewRequestFormsApi.response.results.filter((r) => !r.sample).length > 0 && (
                <Alert variant="warning">
                    <div>以下の依頼書IDはWEB問診未完了です</div>
                    {stillInterviewRequestFormsApi.response.results
                        .filter((r) => !r.sample)
                        .map((r) => {
                            return r.code
                        })
                        .join(', ')}
                </Alert>
            )}
            <ContentBody>
                <TableWrapper contentHeaderCount={0}>
                    <ExTable>
                        <thead>
                            <tr>
                                <TableCell th width={40}>
                                    □
                                </TableCell>
                                <TableCell th width={40}>
                                    #
                                </TableCell>
                                <TableCell th width={200}>
                                    バーコード
                                </TableCell>
                                <TableCell th width={100} align="center">
                                    WEB問診
                                </TableCell>
                                <TableCell th width={300}>
                                    搬送報告
                                </TableCell>
                                <TableCell th width={100}>
                                    尿量(mL)
                                </TableCell>
                                <TableCell th width={100}>
                                    チルド輸送
                                </TableCell>
                                <TableCell th width={120} align="center">
                                    スピッツの状態
                                </TableCell>
                                <TableCell th width={100} align="center">
                                    血液混入
                                </TableCell>
                                <TableCell th width={100} align="center">
                                    尿以外の検体
                                </TableCell>
                                <TableCell th width={140} align="center">
                                    指定容器以外の容器
                                </TableCell>
                                <TableCell th width={120} align="center">
                                    その他の異常
                                </TableCell>
                                <TableCell th width={300}>
                                    備考
                                </TableCell>
                                <TableCell th width={80} align="center">
                                    再採尿
                                </TableCell>
                                <TableCell th width={180}>
                                    ユーザー名
                                </TableCell>
                                <TableCell th width={60}>
                                    性別
                                </TableCell>
                                <TableCell th width={80} align="center">
                                    年齢
                                </TableCell>
                                <TableCell th width={180} align="center">
                                    入庫時メッセージ
                                </TableCell>
                                {isHomeTest && (
                                    <TableCell th width={180} align="center">
                                        採尿時間
                                    </TableCell>
                                )}
                                <TableCell th width={140} align="center">
                                    確認
                                </TableCell>
                                <TableCell th width={140}>
                                    Rack ID
                                </TableCell>
                                <TableCell th width={100}>
                                    TubeLocation
                                </TableCell>
                                <TableCell th width={120} align="center">
                                    入庫済
                                </TableCell>
                                <TableCell th width={80} align="center">
                                    研究同意
                                </TableCell>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                return form.object.map((sample, index) => {
                                    const isInspected = sample.inspectionStatus === 'INSPECTED'
                                    const isPcrSample = !sample.isChilledTransport && sample.patient?.address?.prefecture === '沖縄県'
                                    return (
                                        <tr key={index} style={{ backgroundColor: isInspected ? '#0832' : 'transparent' }}>
                                            <TableCell>
                                                {!isInspected && (
                                                    <CloseButton
                                                        onClick={() => {
                                                            form.update((f) => {
                                                                f.splice(index, 1)
                                                            })
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell className="sticky-table-cell">
                                                <InputField
                                                    form={form}
                                                    attr={[index, 'tubeBarcode']}
                                                    tabIndex={index}
                                                    onChange={(e) => {
                                                        // setTimeoutで500ms後にfocusを次に移す
                                                        setTimeout(() => {
                                                            focusNextElement(index + 1)
                                                        }, 300)
                                                    }}
                                                    onBlur={(e) => {
                                                        const barcode = formatRequestFormCode(form.object[index].tubeBarcode)
                                                        if (!barcode || barcode === '') {
                                                            return
                                                        }
                                                        if (form.object.filter((s) => s.tubeBarcode === barcode).length > 1) {
                                                            form.update((f) => {
                                                                f[index].tubeBarcode = ''
                                                            })
                                                            globalState.setNotificationMessage({ body: 'すでに入力済みです', colorType: 'danger' })
                                                            return
                                                        }
                                                        if (barcode) {
                                                            samplesSearchForm.update((f) => {
                                                                f.requestForm = formatRequestFormCode(barcode)
                                                            })
                                                        }
                                                        const requestForm = stillInterviewRequestFormsApi.response.results.find((r) => r.code === barcode)
                                                        if (requestForm) {
                                                            form.update((f) => {
                                                                f[index].transportationReportId = requestForm.transportationReportId
                                                            })
                                                        }
                                                    }}
                                                    disabled={(index !== 0 && !form.object[index - 1].tubeBarcode) || isInspected}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {!sample.tubeBarcode ? undefined : sample.id ? (
                                                    <Badge bg="success">完了済</Badge>
                                                ) : (
                                                    <Badge bg="danger">未完了</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ position: 'relative' }}>
                                                    <SelectField
                                                        form={form}
                                                        attr={[index, 'transportationReportId']}
                                                        includeBlank
                                                        selectItems={transportationReportsApi.response.results.map((tr) => {
                                                            return {
                                                                label: `ID: ${tr.id} / 伝票番号: ${tr.code}`,
                                                                value: tr.id!,
                                                            }
                                                        })}
                                                    />
                                                    {sample.tubeBarcode && !sample.id && !sample.transportationReportId && (
                                                        <div style={{ fontSize: 10, color: 'red', position: 'absolute', bottom: -18, left: 4 }}>
                                                            未WEB問診の場合は搬送報告まで入力してください
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <InputField type="number" form={form} attr={[index, 'quantity']} step={0.1} disabled={!sample.patient} />
                                            </TableCell>
                                            <TableCell>
                                                <CheckboxField
                                                    form={form}
                                                    attr={[index, 'isChilledTransport']}
                                                    checkboxLabel="チルド輸送"
                                                    onChange={(e) => {
                                                        updateTubeLocations()
                                                    }}
                                                    disabled={isInspected || !sample.id}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'hasNoProblem']}
                                                        checkboxLabel="異常なし"
                                                        onChange={(e) => {
                                                            if (e?.target.checked) {
                                                                form.update((f) => {
                                                                    f[index].includeBlood = false
                                                                    f[index].nonUrine = false
                                                                    f[index].differentContainer = false
                                                                    f[index].otherAbnormalities = false
                                                                })
                                                            }
                                                        }}
                                                        disabled={isInspected || !sample.id}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'includeBlood']}
                                                        checkboxLabel="血液混入"
                                                        onChange={(e) => onChangeException(e, index)}
                                                        disabled={isInspected || !sample.id}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'nonUrine']}
                                                        checkboxLabel="尿以外"
                                                        onChange={(e) => onChangeException(e, index)}
                                                        disabled={isInspected || !sample.id}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'differentContainer']}
                                                        checkboxLabel="容器違い"
                                                        onChange={(e) => onChangeException(e, index)}
                                                        disabled={isInspected || !sample.id}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'otherAbnormalities']}
                                                        checkboxLabel="その他異常"
                                                        onChange={(e) => onChangeException(e, index)}
                                                        disabled={isInspected || !sample.id}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <InputField form={form} attr={[index, 'note']} as="textarea" style={{ height: 40 }} />
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField form={form} attr={[index, 'isRecollect']} checkboxLabel="再採尿" />
                                                </div>
                                            </TableCell>
                                            <TableCell>{form.object[index].patient?.id ? `${form.object[index].patient?.searchName}` : ''}</TableCell>
                                            <TableCell>
                                                {form.object[index].patient?.gender === 'MALE'
                                                    ? '男性'
                                                    : form.object[index].patient?.gender === 'FEMALE'
                                                    ? '女性'
                                                    : ''}
                                            </TableCell>
                                            <TableCell align="center">{form.object[index].background?.age}</TableCell>
                                            <TableCell
                                                align="center"
                                                style={{ backgroundColor: form.object[index].requestForm?.noticeMessage ? '#FEF9C3' : '' }}
                                            >
                                                <div style={{ padding: '2px 4px' }}>{form.object[index].requestForm?.noticeMessage}</div>
                                            </TableCell>
                                            {isHomeTest && (
                                                <TableCell align="center">
                                                    <TimePickerField form={form} attr={[index, 'collectionTime']} />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'checkedPatient']}
                                                        checkboxLabel="確認しました"
                                                        disabled={
                                                            !form.object[index].patient?.id ||
                                                            !isCheckedStatus(index) ||
                                                            form.object[index].checkedPatient ||
                                                            !form.object[index].transportationReportId ||
                                                            isInspected
                                                        }
                                                        onChange={(e) => {
                                                            if (e?.target.checked) {
                                                                updateTubeLocations()
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell style={{ backgroundColor: isPcrSample ? '#ffecee' : '' }}>
                                                {sample.rack?.locationCode ? (
                                                    <Badge bg="success">{sample.rack?.locationCode}</Badge>
                                                ) : form.object[index].checkedPatient ? (
                                                    hasProblem(sample) ? (
                                                        <Badge bg="danger">廃棄</Badge>
                                                    ) : (
                                                        sample.freezerLocation
                                                    )
                                                ) : (
                                                    ''
                                                )}
                                                {isPcrSample && <div style={{ color: 'red' }}>※ PCR用Rack</div>}
                                            </TableCell>
                                            <TableCell style={{ backgroundColor: isPcrSample ? '#ffecee' : '' }}>
                                                {isInspected ? (
                                                    <Badge bg="success">{sample.tubeLocation}</Badge>
                                                ) : form.object[index].checkedPatient ? (
                                                    hasProblem(sample) ? (
                                                        <Badge bg="danger">廃棄</Badge>
                                                    ) : (
                                                        sample.tubeLocation
                                                    )
                                                ) : (
                                                    ''
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {isInspected ? <Badge bg="success">入庫済</Badge> : <Badge bg="danger">未入庫</Badge>}
                                            </TableCell>
                                            <TableCell align="center">
                                                {form.object[index].followupStudyAgreement ? <Badge bg="success">同意</Badge> : <Badge bg="danger">拒否</Badge>}
                                            </TableCell>
                                        </tr>
                                    )
                                })
                            })()}
                        </tbody>
                    </ExTable>
                </TableWrapper>
            </ContentBody>
        </>
    )
}

export default withRouter(ReceiptPage)
